import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, ArrowRight, Upload, X, Check, Save, Sparkles, FileText, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { api, apiErr } from "../../lib/api";
import { AUDIENCES, APP_TYPES, CATEGORIES, ADVANCEMENT } from "../../lib/constants";
import { useLang } from "../../context/LanguageContext";

const EMPTY = {
  title: "", description: "", problem: "", audience: "", app_type: "", category: "",
  has_name: null, app_name: "", slogan: "", logo: null, documents: [],
  advancement: "", similar_apps_exist: "", similar_apps: "",
  differentiation: "", origin: "", advantage: "",
};

const STEP_KEYS = ["u_step1", "u_step2", "u_step3", "u_step4", "u_step5"];

export default function SubmitIdea() {
  const { pid } = useParams();
  const nav = useNavigate();
  const { t } = useLang();
  const STEPS = STEP_KEYS.map((k) => t(k));
  const [step, setStep] = useState(0);
  const [f, setF] = useState(EMPTY);
  const [id, setId] = useState(pid || null);
  const [saving, setSaving] = useState(false);
  const [c1, setC1] = useState(false);
  const [c2, setC2] = useState(false);

  useEffect(() => {
    if (pid) api.get(`/projects/${pid}`).then((r) => { setF({ ...EMPTY, ...r.data }); setId(pid); }).catch(() => {});
  }, [pid]);

  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));

  const persist = async (silent = false) => {
    const payload = { ...f };
    delete payload.id; delete payload.status; delete payload.ref;
    try {
      if (id) {
        await api.put(`/projects/${id}`, payload);
      } else {
        const { data } = await api.post("/projects", payload);
        setId(data.id);
        return data.id;
      }
      if (!silent) toast.success("Brouillon enregistré.");
      return id;
    } catch (e) { toast.error(apiErr(e)); }
  };

  const saveDraft = async () => {
    setSaving(true);
    await persist();
    setSaving(false);
    toast.success("Brouillon enregistré. Vous pouvez continuer plus tard.");
    nav("/app/projets");
  };

  const next = async () => {
    if (step === 0 && (!f.title || !f.description)) return toast.error("Merci de renseigner le titre et la description.");
    await persist(true);
    setStep((s) => Math.min(STEPS.length - 1, s + 1));
    window.scrollTo({ top: 0 });
  };
  const prev = () => setStep((s) => Math.max(0, s - 1));

  const uploadFile = (e, kind) => {
    const files = Array.from(e.target.files || []);
    files.forEach((file) => {
      if (file.size > 3 * 1024 * 1024) return toast.error(`${file.name} dépasse 3 Mo.`);
      const reader = new FileReader();
      reader.onload = () => {
        if (kind === "logo") set("logo", reader.result);
        else set("documents", [...f.documents, { name: file.name, type: file.type, data: reader.result }]);
      };
      reader.readAsDataURL(file);
    });
  };

  const submit = async () => {
    if (!c1 || !c2) return toast.error("Merci de cocher les deux confirmations.");
    setSaving(true);
    const finalId = await persist(true);
    try {
      await api.post(`/projects/${finalId || id}/submit`);
      toast.success("Votre idée a bien été transmise à l'Academy 🎉");
      nav(`/app/projets/${finalId || id}`);
    } catch (e) { toast.error(apiErr(e)); }
    finally { setSaving(false); }
  };

  return (
    <div className="mx-auto max-w-3xl">
      <div className="flex items-center justify-between">
        <button onClick={() => nav("/app")} className="inline-flex items-center gap-1.5 text-sm text-ink-secondary hover:text-white"><ArrowLeft size={15} /> Tableau de bord</button>
        <button onClick={saveDraft} disabled={saving} data-testid="save-draft" className="inline-flex items-center gap-1.5 border border-white/15 px-4 py-2 text-xs text-ink-secondary transition-colors hover:border-cyan hover:text-white">
          <Save size={14} /> {t("u_save_later")}
        </button>
      </div>

      <div className="mt-6">
        <div className="text-[11px] uppercase tracking-[0.25em] text-cyan">{t("u_dep_kicker")}</div>
        <h1 className="mt-3 font-display text-3xl font-900 text-white">{t("u_step")} {step + 1} — {STEPS[step]}</h1>
      </div>

      {/* Progress */}
      <div className="mt-6 flex gap-2">
        {STEPS.map((s, i) => (
          <div key={i} className="flex-1">
            <div className={`h-1 w-full transition-colors ${i <= step ? "bg-cyan" : "bg-elevated"}`} />
            <div className={`mt-2 hidden text-[10px] uppercase tracking-wider sm:block ${i === step ? "text-cyan" : "text-ink-tertiary"}`}>{s}</div>
          </div>
        ))}
      </div>

      <AnimatePresence mode="wait">
        <motion.div key={step} initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} transition={{ duration: 0.3 }} className="mt-10">
          {step === 0 && <Step0 f={f} set={set} />}
          {step === 1 && <Step1 f={f} set={set} />}
          {step === 2 && <Step2 f={f} set={set} uploadFile={uploadFile} />}
          {step === 3 && <Step3 f={f} set={set} />}
          {step === 4 && <Step4 f={f} c1={c1} c2={c2} setC1={setC1} setC2={setC2} />}
        </motion.div>
      </AnimatePresence>

      <div className="mt-12 flex items-center justify-between border-t border-white/10 pt-6">
        <button onClick={prev} disabled={step === 0} data-testid="step-prev" className="inline-flex items-center gap-1.5 text-sm text-ink-secondary transition-colors hover:text-white disabled:opacity-30">
          <ArrowLeft size={15} /> {t("u_prev")}
        </button>
        {step < STEPS.length - 1 ? (
          <button onClick={next} data-testid="step-next" className="group inline-flex items-center gap-2 bg-cyan px-7 py-3.5 text-sm font-600 text-void transition-all hover:-translate-y-0.5 hover:glow-cyan">
            {t("u_continue")} <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />
          </button>
        ) : (
          <button onClick={submit} disabled={saving} data-testid="submit-project" className="group inline-flex items-center gap-2 bg-cyan px-7 py-3.5 text-sm font-700 uppercase tracking-wide text-void transition-all hover:-translate-y-0.5 hover:glow-cyan disabled:opacity-60">
            {saving ? t("u_sending") : t("u_send")} <Sparkles size={16} />
          </button>
        )}
      </div>
    </div>
  );
}

/* ---------- Steps ---------- */
const Input = ({ label, help, value, onChange, testid, placeholder, autoFocus }) => (
  <label className="block">
    <span className="mb-1.5 block text-sm font-500 text-white">{label}</span>
    {help && <span className="mb-2 block text-xs text-ink-tertiary">{help}</span>}
    <input value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} data-testid={testid} autoFocus={autoFocus}
      className="w-full rounded-xl border border-white/15 bg-white/[0.06] px-4 py-3.5 text-sm text-white placeholder:text-ink-tertiary outline-none transition-colors focus:border-cyan focus:ring-1 focus:ring-cyan" />
  </label>
);
const Area = ({ label, help, value, onChange, testid, placeholder, rows = 4 }) => (
  <label className="block">
    <span className="mb-1.5 block text-sm font-500 text-white">{label}</span>
    {help && <span className="mb-2 block text-xs text-ink-tertiary">{help}</span>}
    <textarea rows={rows} value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} data-testid={testid}
      className="w-full resize-none rounded-xl border border-white/15 bg-white/[0.06] px-4 py-3.5 text-sm text-white placeholder:text-ink-tertiary outline-none transition-colors focus:border-cyan focus:ring-1 focus:ring-cyan" />
  </label>
);
const Chips = ({ label, options, value, onChange, testidPrefix }) => (
  <div>
    <span className="mb-3 block text-sm font-500 text-white">{label}</span>
    <div className="flex flex-wrap gap-2">
      {options.map((o) => (
        <button key={o} type="button" onClick={() => onChange(o)} data-testid={`${testidPrefix}-${o}`}
          className={`border px-4 py-2 text-sm transition-all hover:-translate-y-0.5 ${value === o ? "border-cyan bg-cyan text-void" : "border-white/15 text-ink-secondary hover:text-white"}`}>
          {o}
        </button>
      ))}
    </div>
  </div>
);

const Step0 = ({ f, set }) => (
  <div className="space-y-8">
    <Input label="Quel est le titre de votre idée ?" value={f.title} onChange={(v) => set("title", v)} testid="field-title" autoFocus
      placeholder="Ex : Retrouver facilement un artisan disponible autour de moi." />
    <Area label="Expliquez-nous simplement votre idée" rows={5}
      help="Expliquez votre idée avec vos propres mots. Vous n'avez pas besoin de connaître la programmation."
      value={f.description} onChange={(v) => set("description", v)} testid="field-description" />
    <Area label="Quel problème votre application doit-elle résoudre ?" value={f.problem} onChange={(v) => set("problem", v)} testid="field-problem" />
    <Chips label="À qui s'adresse votre application ?" options={AUDIENCES} value={f.audience} onChange={(v) => set("audience", v)} testidPrefix="audience" />
    <Chips label="Quel type d'application imaginez-vous ?" options={APP_TYPES} value={f.app_type} onChange={(v) => set("app_type", v)} testidPrefix="apptype" />
    <Chips label="Catégorie" options={CATEGORIES} value={f.category} onChange={(v) => set("category", v)} testidPrefix="category" />
  </div>
);

const Step1 = ({ f, set }) => (
  <div className="space-y-8">
    <div>
      <span className="mb-3 block text-sm font-500 text-white">Avez-vous déjà un nom pour votre application ?</span>
      <div className="flex gap-2">
        {[["Oui", true], ["Non", false]].map(([l, v]) => (
          <button key={l} type="button" onClick={() => set("has_name", v)} data-testid={`hasname-${l}`}
            className={`border px-6 py-2.5 text-sm transition-all ${f.has_name === v ? "border-cyan bg-cyan text-void" : "border-white/15 text-ink-secondary hover:text-white"}`}>{l}</button>
        ))}
      </div>
    </div>
    {f.has_name && <Input label="Nom proposé de l'application" value={f.app_name} onChange={(v) => set("app_name", v)} testid="field-appname" />}
    <Input label="Avez-vous déjà un slogan ? (facultatif)" value={f.slogan} onChange={(v) => set("slogan", v)} testid="field-slogan" placeholder="Slogan proposé" />
  </div>
);

const Step2 = ({ f, set, uploadFile }) => (
  <div className="space-y-10">
    <div>
      <span className="mb-2 block text-sm font-500 text-white">Avez-vous déjà imaginé ou créé un logo ?</span>
      <span className="mb-4 block text-xs text-ink-tertiary">Formats : PNG, JPG, JPEG, SVG, PDF · max 3 Mo</span>
      {f.logo ? (
        <div className="flex items-center gap-4">
          <img src={f.logo} alt="logo" className="h-24 w-24 border border-white/10 object-contain p-2" />
          <button type="button" onClick={() => set("logo", null)} data-testid="remove-logo" className="inline-flex items-center gap-1.5 text-sm text-ink-secondary hover:text-white"><X size={15} /> Supprimer</button>
        </div>
      ) : (
        <label data-testid="upload-logo" className="flex cursor-pointer flex-col items-center justify-center gap-2 border border-dashed border-white/20 bg-surface py-10 text-center transition-colors hover:border-cyan">
          <Upload className="text-cyan" />
          <span className="text-sm text-white">Ajouter mon logo</span>
          <input type="file" accept="image/*,.pdf,.svg" onChange={(e) => uploadFile(e, "logo")} className="hidden" />
        </label>
      )}
    </div>

    <div>
      <span className="mb-2 block text-sm font-500 text-white">Ajouter des documents ou visuels</span>
      <span className="mb-4 block text-xs text-ink-tertiary">Croquis, maquettes, captures, PDF... Tout ce qui aide à mieux comprendre votre idée.</span>
      <label data-testid="upload-docs" className="flex cursor-pointer items-center justify-center gap-2 border border-dashed border-white/20 bg-surface py-6 text-sm text-white transition-colors hover:border-cyan">
        <FileText size={16} className="text-cyan" /> Ajouter des fichiers
        <input type="file" multiple accept="image/*,.pdf,.svg,.docx,.pptx" onChange={(e) => uploadFile(e, "docs")} className="hidden" />
      </label>
      {f.documents?.length > 0 && (
        <div className="mt-4 space-y-2">
          {f.documents.map((d, i) => (
            <div key={i} className="flex items-center justify-between border border-white/10 bg-surface px-4 py-2.5 text-sm">
              <span className="text-white line-clamp-1">{d.name}</span>
              <button type="button" onClick={() => set("documents", f.documents.filter((_, j) => j !== i))} data-testid={`remove-doc-${i}`}><X size={15} className="text-ink-secondary hover:text-white" /></button>
            </div>
          ))}
        </div>
      )}
    </div>
  </div>
);

const Step3 = ({ f, set }) => (
  <div className="space-y-8">
    <Chips label="Où en êtes-vous aujourd'hui ?" options={ADVANCEMENT} value={f.advancement} onChange={(v) => set("advancement", v)} testidPrefix="advancement" />
    <Chips label="Existe-t-il déjà des applications similaires ?" options={["Oui", "Non", "Je ne sais pas"]} value={f.similar_apps_exist} onChange={(v) => set("similar_apps_exist", v)} testidPrefix="similar" />
    {f.similar_apps_exist === "Oui" && <Input label="Citez ces applications" value={f.similar_apps} onChange={(v) => set("similar_apps", v)} testid="field-similar-apps" />}
    <Area label="Pourquoi votre idée est-elle différente ? (facultatif)" value={f.differentiation} onChange={(v) => set("differentiation", v)} testid="field-diff" />
    <Area label="Comment avez-vous eu cette idée ? (facultatif)" value={f.origin} onChange={(v) => set("origin", v)} testid="field-origin" />
    <Area label="Quel est le principal avantage de votre projet ? (facultatif)" value={f.advantage} onChange={(v) => set("advantage", v)} testid="field-advantage" />
  </div>
);

const Step4 = ({ f, c1, c2, setC1, setC2 }) => (
  <div className="space-y-8">
    <div className="border border-cyan/30 bg-cyan/5 p-6">
      <div className="flex items-center gap-2 font-display text-lg font-700 text-white"><ShieldCheck size={20} className="text-cyan" /> Votre idée reste confidentielle</div>
      <p className="mt-2 text-sm text-ink-secondary">Les informations déposées dans votre dossier ne sont pas rendues publiques sans votre autorisation.</p>
    </div>

    <div className="border border-white/10 bg-surface p-6">
      <div className="mb-4 text-[11px] uppercase tracking-[0.2em] text-ink-tertiary">Mon projet — récapitulatif</div>
      <Row l="Titre de l'idée" v={f.title} />
      <Row l="Nom proposé" v={f.app_name || "—"} />
      <Row l="Slogan" v={f.slogan || "—"} />
      <Row l="Catégorie" v={f.category || "—"} />
      <Row l="Public visé" v={f.audience || "—"} />
      <Row l="Logo" v={f.logo ? "Ajouté ✓" : "—"} />
      <Row l="Documents joints" v={f.documents?.length ? `${f.documents.length} fichier(s)` : "—"} />
    </div>

    <div className="space-y-3">
      {[[c1, setC1, "Je confirme être à l'origine de cette idée ou être autorisé à la présenter.", "confirm-1"],
        [c2, setC2, "J'accepte les conditions de participation d'APP CHALLENGE ACADEMY.", "confirm-2"]].map(([val, setter, txt, tid], i) => (
        <button key={i} type="button" onClick={() => setter(!val)} data-testid={tid} className="flex w-full items-start gap-3 text-left">
          <span className={`mt-0.5 flex h-5 w-5 flex-shrink-0 items-center justify-center border ${val ? "border-cyan bg-cyan text-void" : "border-white/20"}`}>{val && <Check size={13} />}</span>
          <span className="text-sm text-ink-secondary">{txt}</span>
        </button>
      ))}
    </div>
  </div>
);

const Row = ({ l, v }) => (
  <div className="flex justify-between gap-4 border-b border-white/5 py-2.5 text-sm last:border-0">
    <span className="text-ink-tertiary">{l}</span>
    <span className="text-right text-white line-clamp-1">{v}</span>
  </div>
);
